import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FaEye, FaReceipt, FaUndo, FaBan, FaTimes, FaPrint } from "react-icons/fa";
import API_BASE from "../apiConfig";
import { useTheme, getT } from "../ThemeContext";
import { useAppConfig } from "../AppContext";
import { useSort } from "../hooks/useSort";
import { useDateTimeFormat } from "../utils/formatters";
import StatusBadge from "../components/StatusBadge";
import { PrimaryButton, GhostButton, DangerButton } from "../components/ThemedButton";
import SixersLogo from "../Sixers_Logo.png";

const money = (n, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(n || 0));

const CURRENCY_SYMBOLS = { USD: "$", AUD: "$", GBP: "£", EUR: "€", INR: "₹", CAD: "$", NZD: "$" };

async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Request failed (${res.status})`);
  }
  return data;
}

const STATUS_OPTIONS = [
  "draft", "open", "paid", "partially_paid", "pending",
  "fulfilled", "partially_fulfilled", "cancelled", "refunded", "partially_refunded",
];

const REFUNDABLE_STATUSES = ["paid", "fulfilled", "partially_refunded"];
const NON_CANCELLABLE_STATUSES = ["paid", "fulfilled", "refunded", "partially_refunded", "cancelled"];

// Maps a raw order status to the shared StatusBadge's semantic tones —
// replaces the old plain-colored-text rendering (no pill, no shape at all)
// so order status here reads the same "tinted chip + dot" way status does
// everywhere else in the app, instead of being POS's own one-off look.
function statusTone(status) {
  if (status === "paid" || status === "fulfilled") return "success";
  if (status === "cancelled") return "neutral";
  if (status === "refunded" || status === "partially_refunded") return "danger";
  if (status === "partially_paid" || status === "partially_fulfilled" || status === "pending") return "warning";
  return "neutral";
}

function statusLabel(status) {
  return String(status || "").split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export default function POSOrders({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { config } = useAppConfig();
  const currency = (config?.currency || "USD").toUpperCase();
  const { fmtDateTime } = useDateTimeFormat();

  const canRefund = ["Staff", "Admin", "Super Admin"].includes(currentUser?.user_role);
  const canCancel = !!currentUser?.user_role; // any authenticated staff role

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const [refundOpen, setRefundOpen] = useState(false);
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundError, setRefundError] = useState("");
  const [refundMode, setRefundMode] = useState("full"); // full | partial
  const [refundAmount, setRefundAmount] = useState("");
  const [refundItemId, setRefundItemId] = useState("");
  const [refundQty, setRefundQty] = useState(1);
  const [refundMethod, setRefundMethod] = useState("original");
  const [refundReason, setRefundReason] = useState("");

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const [receipt, setReceipt] = useState(null);
  const [receiptBusy, setReceiptBusy] = useState(false);

  // Hooks must run unconditionally, before any early return.
  const { sorted, toggleSort, sortArrow, thSort } = useSort(orders, "created_at", "desc");

  useEffect(() => {
    api("/pos/locations/").then((d) => setLocations(d.locations || [])).catch(() => {});
  }, []);

  const loadOrders = useCallback(() => {
    setLoading(true); setError("");
    const params = new URLSearchParams();
    if (locationId) params.set("location_id", locationId);
    if (statusFilter) params.set("status", statusFilter);
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    if (search) params.set("q", search);
    api(`/pos/orders/?${params.toString()}`)
      .then((d) => setOrders(d.orders || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [locationId, statusFilter, startDate, endDate, search]);

  useEffect(() => {
    const t = setTimeout(loadOrders, 250);
    return () => clearTimeout(t);
  }, [loadOrders]);

  function openOrder(orderId) {
    setDetailLoading(true); setDetailError(""); setSelectedOrder(null); setReceipt(null);
    api(`/pos/orders/${orderId}`)
      .then((d) => setSelectedOrder(d.order))
      .catch((e) => setDetailError(e.message))
      .finally(() => setDetailLoading(false));
  }

  function closeDetail() {
    setSelectedOrder(null); setDetailError(""); setRefundOpen(false); setCancelOpen(false); setReceipt(null);
  }

  function startRefund() {
    setRefundMode("full");
    setRefundAmount(String(remainingRefundable(selectedOrder)));
    setRefundItemId("");
    setRefundQty(1);
    setRefundMethod("original");
    setRefundReason("");
    setRefundError("");
    setRefundOpen(true);
  }

  function remainingRefundable(order) {
    if (!order) return 0;
    const refunded = (order.refunds || []).reduce((s, r) => s + Number(r.amount || 0), 0);
    return Math.max(0, Number(order.grand_total || 0) - refunded);
  }

  // An order-level discount is never split back into each item's own
  // line_total (only the order's discount_total reflects it) — so an item's
  // line_total is its full *listed* price, not what the customer actually
  // paid for it. Scale every item down by the order's overall
  // grand_total/Σline_total ratio to get each item's true net-paid share,
  // the same "order pool" approach the tax report uses. Without this,
  // refunding an item "in full" hands back more than the customer paid.
  function itemUnitValue(order, item) {
    const items = order.items || [];
    const sumLineTotal = items.reduce((s, it) => s + Number(it.line_total || 0), 0);
    const scale = sumLineTotal ? Number(order.grand_total || 0) / sumLineTotal : 1;
    return (Number(item.line_total || 0) * scale) / (item.quantity || 1);
  }

  // Units of this line item not yet returned by a prior refund.
  function itemRemainingQty(order, item) {
    const refundedQty = (order.refunds || [])
      .filter((r) => r.order_item_id === item.item_id)
      .reduce((s, r) => s + Number(r.quantity || 0), 0);
    return Math.max(0, (item.quantity || 0) - refundedQty);
  }

  // Tax portion of the amount currently being refunded. The item's
  // tax_amount itself isn't discounted (this app taxes the pre-discount
  // price), but it's earned per-unit, so scale it by the fraction of the
  // item's quantity being refunded.
  function estimatedTaxRefund(order, amount) {
    if (!order || !amount) return 0;
    if (refundMode === "partial" && refundItemId) {
      const item = (order.items || []).find((it) => String(it.item_id) === String(refundItemId));
      if (!item || !item.quantity) return 0;
      const qtyRatio = Math.min(refundQty, item.quantity) / item.quantity;
      return Number(item.tax_amount) * qtyRatio;
    }
    if (!Number(order.grand_total)) return 0;
    const rate = Number(order.tax_total) / Number(order.grand_total);
    return amount * rate;
  }

  async function submitRefund() {
    if (!selectedOrder) return;
    const isItemRefund = refundMode === "partial" && refundItemId;
    const amount = parseFloat(refundAmount || "0");
    if (!isItemRefund && (!amount || amount <= 0)) { setRefundError("Enter a refund amount greater than zero."); return; }
    if (isItemRefund && (!refundQty || refundQty <= 0)) { setRefundError("Enter a quantity to refund."); return; }
    const confirmAmount = isItemRefund ? amount : amount;
    if (!window.confirm(`Refund ${money(confirmAmount, currency)} for order ${selectedOrder.order_number}? This cannot be undone.`)) return;
    setRefundBusy(true); setRefundError("");
    try {
      await api(`/pos/orders/${selectedOrder.order_id}/refund`, {
        method: "POST",
        body: JSON.stringify({
          order_item_id: isItemRefund ? Number(refundItemId) : null,
          quantity: isItemRefund ? refundQty : null,
          amount: isItemRefund ? null : amount,
          method: refundMethod,
          reason: refundReason || null,
        }),
      });
      setRefundOpen(false);
      openOrder(selectedOrder.order_id);
      loadOrders();
    } catch (e) {
      setRefundError(e.message);
    } finally {
      setRefundBusy(false);
    }
  }

  async function submitCancel() {
    if (!selectedOrder) return;
    if (!window.confirm(`Cancel order ${selectedOrder.order_number}? This cannot be undone.`)) return;
    setCancelBusy(true);
    try {
      await api(`/pos/orders/${selectedOrder.order_id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason: cancelReason || null }),
      });
      setCancelOpen(false);
      openOrder(selectedOrder.order_id);
      loadOrders();
    } catch (e) {
      setDetailError(e.message);
    } finally {
      setCancelBusy(false);
    }
  }

  // Fetches and opens the receipt directly — used both by the order-detail
  // modal's "View Receipt" button (orderId omitted, uses selectedOrder) and
  // by the dedicated receipt icon in the orders table (orderId given), which
  // skips the order-detail screen entirely rather than routing through it.
  async function viewReceipt(orderId) {
    const id = orderId || selectedOrder?.order_id;
    if (!id) return;
    setReceiptBusy(true);
    try {
      const d = await api(`/pos/orders/${id}/receipt`);
      setReceipt(d.receipt);
    } catch (e) {
      setError(e.message);
    } finally {
      setReceiptBusy(false);
    }
  }

  function closeReceipt() {
    setReceipt(null);
  }

  // Print only the receipt card, not the app around it — the receipt is
  // rendered via a portal directly under <body> (see below), and the
  // .printing-receipt class (removed again once the print dialog closes)
  // hides #root for the duration, per the print rule in index.css.
  function handlePrintReceipt() {
    document.body.classList.add("printing-receipt");
    const cleanup = () => document.body.classList.remove("printing-receipt");
    window.addEventListener("afterprint", cleanup, { once: true });
    window.print();
    setTimeout(cleanup, 2000); // fallback for browsers that don't fire afterprint reliably
  }

  // Same plain button styling as POSInventory.js's Actions column — kept
  // consistent across the POS screens rather than a heavier, one-off look.
  const card = { background: T.glass, border: `1px solid ${T.glassBdr}`, borderRadius: 14, padding: 16 };
  const input = { padding: "8px 10px", borderRadius: 8, fontSize: 13, border: `1px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText };
  const th = { textAlign: "left", padding: "10px 8px", fontSize: 12, color: T.textSub, borderBottom: `1px solid ${T.divider}` };
  const td = { padding: "10px 8px", fontSize: 13, borderBottom: `1px solid ${T.divider}` };

  return (
    <div style={{ color: T.textPrimary }}>
      <h2 style={{ marginTop: 0 }}>Orders &amp; Refunds</h2>

      <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", marginBottom: 16 }}>
        <div>
          <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Location</label>
          <select style={input} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Status</label>
          <select style={input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>From</label>
          <input type="date" style={input} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>To</label>
          <input type="date" style={input} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Search order #, customer, or item</label>
          <input style={{ ...input, width: "100%", boxSizing: "border-box" }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. Cricket Bat" />
        </div>
        <GhostButton size="sm" onClick={loadOrders}>{loading ? "Loading…" : "Refresh"}</GhostButton>
      </div>

      {error && <div style={{ color: T.red, marginBottom: 10 }}>{error}</div>}

      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>Amounts in {currency} ({CURRENCY_SYMBOLS[currency] || "$"})</div>

      <div style={{ ...card, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, ...thSort("order_number", T) }} onClick={() => toggleSort("order_number")}>Order #{sortArrow("order_number")}</th>
              <th style={{ ...th, ...thSort("created_at", T) }} onClick={() => toggleSort("created_at")}>Date/Time{sortArrow("created_at")}</th>
              <th style={{ ...th, ...thSort("location_name", T) }} onClick={() => toggleSort("location_name")}>Location{sortArrow("location_name")}</th>
              <th style={{ ...th, ...thSort("customer_name", T) }} onClick={() => toggleSort("customer_name")}>Customer{sortArrow("customer_name")}</th>
              <th style={{ ...th, ...thSort("item_count", T) }} onClick={() => toggleSort("item_count")}>Items{sortArrow("item_count")}</th>
              <th style={{ ...th, ...thSort("grand_total", T) }} onClick={() => toggleSort("grand_total")}>Total{sortArrow("grand_total")}</th>
              <th style={{ ...th, ...thSort("refunded_total", T) }} onClick={() => toggleSort("refunded_total")}>Refund{sortArrow("refunded_total")}</th>
              <th style={{ ...th, ...thSort("status", T) }} onClick={() => toggleSort("status")}>Status{sortArrow("status")}</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((o) => {
              const refunded = Number(o.refunded_total || 0);
              const balance = Math.max(0, Number(o.grand_total || 0) - refunded);
              return (
                <tr key={o.order_id} style={{ cursor: "pointer" }} onClick={() => openOrder(o.order_id)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = T.rowHoverBg || "rgba(127,127,127,0.08)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  <td style={{ ...td, fontWeight: 700 }}>{o.order_number}</td>
                  <td style={td}>{fmtDateTime(o.created_at)}</td>
                  <td style={td}>{o.location_name || "—"}</td>
                  <td style={td}>{o.customer_name || "Walk-in"}</td>
                  <td style={td}>{o.item_count}</td>
                  <td style={td}>
                    {refunded > 0 ? (
                      <>
                        <span style={{ textDecoration: "line-through", opacity: 0.55 }}>{money(o.grand_total, currency)}</span>{" "}
                        <span style={{ fontWeight: 700 }}>{money(balance, currency)}</span>
                      </>
                    ) : money(o.grand_total, currency)}
                  </td>
                  <td style={td}>
                    {refunded > 0 ? (
                      <span style={{ fontWeight: 700, color: T.red }}>-{money(refunded, currency)}</span>
                    ) : (
                      <span style={{ color: T.textMuted }}>—</span>
                    )}
                  </td>
                  <td style={td}>
                    <StatusBadge label={statusLabel(o.status)} tone={statusTone(o.status)} />
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <GhostButton size="sm" style={{ marginRight: 6 }} onClick={(e) => { e.stopPropagation(); openOrder(o.order_id); }}>
                      <FaEye size={12} /> View
                    </GhostButton>
                    <GhostButton size="sm" onClick={(e) => { e.stopPropagation(); viewReceipt(o.order_id); }}>
                      <FaReceipt size={12} /> Receipt
                    </GhostButton>
                  </td>
                </tr>
              );
            })}
            {!loading && !sorted.length && (
              <tr><td style={{ ...td, color: T.textMuted }} colSpan={9}>No orders found for the selected filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(selectedOrder || detailLoading || detailError) && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}
             onClick={closeDetail}>
          <div style={{ ...card, width: 560, maxHeight: "85vh", overflowY: "auto", background: isDark ? "#0f2436" : "#fff" }}
               onClick={(e) => e.stopPropagation()}>
            {detailLoading && <div>Loading order…</div>}
            {detailError && <div style={{ color: T.red }}>{detailError}</div>}
            {selectedOrder && !detailLoading && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <h3 style={{ margin: 0 }}>{selectedOrder.order_number}</h3>
                    <div style={{ color: T.textSub, fontSize: 12, marginTop: 4 }}>{fmtDateTime(selectedOrder.created_at)}</div>
                  </div>
                  <StatusBadge label={statusLabel(selectedOrder.status)} tone={statusTone(selectedOrder.status)} style={{ fontSize: 12 }} />
                </div>

                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6, fontWeight: 700 }}>ITEMS</div>
                  {(selectedOrder.items || []).map((it) => (
                    <div key={it.item_id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.divider}`, fontSize: 13 }}>
                      <div>
                        <div>{it.description}</div>
                        <div style={{ color: T.textMuted, fontSize: 11 }}>{money(it.unit_price, currency)} × {it.quantity}</div>
                      </div>
                      <div>{money(it.line_total, currency)}</div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.textSub }}><span>Subtotal</span><span>{money(selectedOrder.subtotal, currency)}</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.textSub }}><span>Discount</span><span>-{money(selectedOrder.discount_total, currency)}</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.textSub }}><span>Tax</span><span>{money(selectedOrder.tax_total, currency)}</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, fontSize: 18, marginTop: 6 }}><span>Total</span><span>{money(selectedOrder.grand_total, currency)}</span></div>
                </div>

                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6, fontWeight: 700 }}>PAYMENTS</div>
                  {(selectedOrder.payments || []).length === 0 && <div style={{ color: T.textMuted, fontSize: 12 }}>No payments recorded.</div>}
                  {(selectedOrder.payments || []).map((p) => (
                    <div key={p.payment_id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}>
                      <span>{p.method} — {p.status}</span><span>{money(p.amount, currency)}</span>
                    </div>
                  ))}
                </div>

                {(selectedOrder.refunds || []).length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6, fontWeight: 700 }}>REFUNDS</div>
                    {selectedOrder.refunds.map((r) => (
                      <div key={r.refund_id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}>
                        <span>{r.method}{r.reason ? ` — ${r.reason}` : ""}</span><span>{money(r.amount, currency)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ marginTop: 14, fontSize: 12, color: T.textSub }}>
                  <div>Customer: {selectedOrder.customer_mapping_id ? "on file" : "Walk-in"}</div>
                  <div>Register session: {selectedOrder.register_session_id || "—"}</div>
                  {selectedOrder.notes && <div>Notes: {selectedOrder.notes}</div>}
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
                  <GhostButton size="sm" onClick={() => viewReceipt()} disabled={receiptBusy}>
                    <FaReceipt size={12} /> {receiptBusy ? "Loading…" : "View Receipt"}
                  </GhostButton>
                  {canRefund && REFUNDABLE_STATUSES.includes(selectedOrder.status) && remainingRefundable(selectedOrder) > 0 && (
                    <GhostButton size="sm" onClick={startRefund}><FaUndo size={12} /> Refund</GhostButton>
                  )}
                  {canCancel && !NON_CANCELLABLE_STATUSES.includes(selectedOrder.status) && (
                    <DangerButton size="sm" onClick={() => setCancelOpen(true)}><FaBan size={12} /> Cancel Order</DangerButton>
                  )}
                  <GhostButton size="sm" style={{ marginLeft: "auto" }} onClick={closeDetail}><FaTimes size={12} /> Close</GhostButton>
                </div>

                {refundOpen && (
                  <div style={{ marginTop: 16, borderTop: `1px solid ${T.divider}`, paddingTop: 14 }}>
                    <div style={{ fontWeight: 800, marginBottom: 8 }}>Issue Refund</div>
                    {refundError && <div style={{ color: T.red, marginBottom: 8, fontSize: 13 }}>{refundError}</div>}
                    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                      <label style={{ fontSize: 12 }}>
                        <input type="radio" checked={refundMode === "full"} onChange={() => { setRefundMode("full"); setRefundAmount(String(remainingRefundable(selectedOrder))); setRefundItemId(""); }} /> Full refund
                      </label>
                      <label style={{ fontSize: 12 }}>
                        <input type="radio" checked={refundMode === "partial"} onChange={() => setRefundMode("partial")} /> Partial refund (by item)
                      </label>
                    </div>
                    {refundMode === "partial" && (
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Item</label>
                        <select
                          style={{ ...input, width: "100%", boxSizing: "border-box" }}
                          value={refundItemId}
                          onChange={(e) => {
                            const itemId = e.target.value;
                            setRefundItemId(itemId);
                            const item = (selectedOrder.items || []).find((it) => String(it.item_id) === itemId);
                            const remQty = item ? itemRemainingQty(selectedOrder, item) : 0;
                            const qty = Math.min(1, remQty) || (remQty > 0 ? 1 : 0);
                            setRefundQty(qty);
                            setRefundAmount(item ? (itemUnitValue(selectedOrder, item) * qty).toFixed(2) : "");
                          }}
                        >
                          <option value="">Select an item…</option>
                          {(selectedOrder.items || []).map((it) => {
                            const remQty = itemRemainingQty(selectedOrder, it);
                            return (
                              <option key={it.item_id} value={it.item_id} disabled={remQty <= 0}>
                                {it.description} — {remQty} of {it.quantity} refundable
                                {remQty <= 0 ? " (fully refunded)" : ""}
                              </option>
                            );
                          })}
                        </select>
                        {refundItemId && selectedOrder.discount_total > 0 && (
                          <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>
                            This order had a discount applied, so each unit's refund is prorated below its listed price — the customer didn't pay full price for it.
                          </div>
                        )}
                      </div>
                    )}
                    {refundMode === "partial" && refundItemId && (() => {
                      const item = (selectedOrder.items || []).find((it) => String(it.item_id) === String(refundItemId));
                      const remQty = item ? itemRemainingQty(selectedOrder, item) : 0;
                      const unitValue = item ? itemUnitValue(selectedOrder, item) : 0;
                      return (
                        <div style={{ marginBottom: 10 }}>
                          <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Quantity to refund (max {remQty})</label>
                          <input
                            type="number" min={1} max={remQty} step={1}
                            style={{ ...input, width: "100%", boxSizing: "border-box" }}
                            value={refundQty}
                            onChange={(e) => {
                              const qty = Math.max(1, Math.min(remQty, parseInt(e.target.value, 10) || 1));
                              setRefundQty(qty);
                              setRefundAmount((unitValue * qty).toFixed(2));
                            }}
                          />
                        </div>
                      );
                    })()}
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Amount</label>
                      <input type="number" step="0.01" style={{ ...input, width: "100%", boxSizing: "border-box" }}
                             value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
                             disabled={refundMode === "full" || (refundMode === "partial" && !!refundItemId)} />
                      {refundMode === "partial" && refundItemId && (
                        <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>Calculated from quantity — not editable.</div>
                      )}
                      {Number(refundAmount) > 0 && (
                        <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>
                          Includes an estimated {money(estimatedTaxRefund(selectedOrder, Number(refundAmount)), currency)} in tax, based on the receipt.
                        </div>
                      )}
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Refund method</label>
                      <select style={{ ...input, width: "100%", boxSizing: "border-box" }} value={refundMethod} onChange={(e) => setRefundMethod(e.target.value)}>
                        <option value="original">Original payment method</option>
                        <option value="cash">Cash</option>
                      </select>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Reason</label>
                      <input style={{ ...input, width: "100%", boxSizing: "border-box" }} value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="Optional" />
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <PrimaryButton disabled={refundBusy} onClick={submitRefund}>{refundBusy ? "Processing…" : "Confirm Refund"}</PrimaryButton>
                      <GhostButton onClick={() => setRefundOpen(false)}>Cancel</GhostButton>
                    </div>
                  </div>
                )}

                {cancelOpen && (
                  <div style={{ marginTop: 16, borderTop: `1px solid ${T.divider}`, paddingTop: 14 }}>
                    <div style={{ fontWeight: 800, marginBottom: 8 }}>Cancel Order</div>
                    <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Reason</label>
                    <input style={{ ...input, width: "100%", boxSizing: "border-box", marginBottom: 12 }} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Optional" />
                    <div style={{ display: "flex", gap: 8 }}>
                      <DangerButton disabled={cancelBusy} onClick={submitCancel}>{cancelBusy ? "Cancelling…" : "Confirm Cancel"}</DangerButton>
                      <GhostButton onClick={() => setCancelOpen(false)}>Back</GhostButton>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {receipt && createPortal(
        <div id="pos-receipt-print-root" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
             onClick={closeReceipt}>
          <div
            style={{
              width: 380, maxHeight: "92vh", overflowY: "auto", background: isDark ? "#18160f" : "#fffdf8",
              color: isDark ? "#e8e6df" : "#1a1a1a", borderRadius: 10, boxShadow: "0 24px 70px rgba(0,0,0,0.45)",
              border: `1px solid ${isDark ? "#3a3524" : "#e8e0c8"}`,
              fontFamily: "'Courier New', monospace", padding: "30px 26px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: "center", marginBottom: 14 }}>
              <img src={config?.logo_url || SixersLogo} alt="" style={{ width: 48, height: 48, objectFit: "contain", marginBottom: 8 }} />
              <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: 0.5 }}>{receipt.org_name}</div>
              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 3, textTransform: "uppercase", letterSpacing: 1 }}>Order Receipt</div>
            </div>

            <div style={{ borderTop: "1px dashed currentColor", margin: "14px 0" }} />

            <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ opacity: 0.65 }}>Order #</span><span style={{ fontWeight: 700 }}>{receipt.order_number}</span>
            </div>
            <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ opacity: 0.65 }}>Date</span><span>{fmtDateTime(receipt.created_at)}</span>
            </div>
            {receipt.customer && (
              <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between" }}>
                <span style={{ opacity: 0.65 }}>Customer</span><span>{receipt.customer.display_name}</span>
              </div>
            )}

            <div style={{ borderTop: "1px dashed currentColor", margin: "14px 0" }} />

            {receipt.items.map((it, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700 }}>
                  <span style={{ paddingRight: 10 }}>{it.description}</span>
                  <span style={{ whiteSpace: "nowrap" }}>{money(it.line_total, receipt.currency)}</span>
                </div>
                <div style={{ fontSize: 11, opacity: 0.6 }}>{it.quantity} × {money(it.unit_price, receipt.currency)}</div>
              </div>
            ))}

            <div style={{ borderTop: "1px dashed currentColor", margin: "14px 0" }} />

            <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span>Subtotal</span><span>{money(receipt.subtotal, receipt.currency)}</span>
            </div>
            {receipt.discount_total > 0 && (
              <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                <span>Discount</span><span>-{money(receipt.discount_total, receipt.currency)}</span>
              </div>
            )}
            <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span>Tax</span><span>{money(receipt.tax_total, receipt.currency)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 17, fontWeight: 800, marginTop: 8, paddingTop: 8, borderTop: "1px solid currentColor" }}>
              <span>TOTAL</span><span>{money(receipt.grand_total, receipt.currency)}</span>
            </div>

            <div style={{ borderTop: "1px dashed currentColor", margin: "14px 0" }} />

            {(receipt.payments || []).map((p, i) => (
              <div key={i} style={{ fontSize: 12, display: "flex", justifyContent: "space-between", marginBottom: 3, textTransform: "capitalize" }}>
                <span>{p.method}</span><span>{money(p.amount, receipt.currency)}</span>
              </div>
            ))}

            <div style={{ textAlign: "center", marginTop: 22, fontSize: 12, opacity: 0.7 }}>
              ★ Thank you for your purchase! ★
            </div>

            <div className="no-print" style={{ display: "flex", gap: 8, marginTop: 20 }}>
              <GhostButton style={{ flex: 1, justifyContent: "center", fontFamily: "inherit" }} onClick={handlePrintReceipt}>
                <FaPrint size={12} /> Print
              </GhostButton>
              <PrimaryButton style={{ flex: 1, justifyContent: "center", fontFamily: "inherit" }} onClick={closeReceipt}>
                Close
              </PrimaryButton>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
